import type { HTMLAttributes, ReactNode, Ref } from "react";

import {
  actions as actionsClass,
  header,
  subtitle as subtitleClass,
  title as titleClass,
  titleBlock,
} from "~/components/PageHeading.css";

export interface PageHeadingProps
  // Omit the native `title` attribute (which is a tooltip string) so
  // our ReactNode-valued `title` prop below doesn't collide with it.
  extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  /** Main page title — rendered as <h1>. */
  title: ReactNode;
  /** Optional one-line subtitle under the title. */
  subtitle?: ReactNode;
  /** Optional right-side actions (usually buttons or links). */
  actions?: ReactNode;
  ref?: Ref<HTMLElement>;
}

/**
 * Standard page-top header. Title on the left, optional subtitle
 * below it, optional actions on the right. Use on every main page
 * to keep the header treatment consistent.
 */
export function PageHeading({
  title,
  subtitle,
  actions,
  className,
  ref,
  ...rest
}: PageHeadingProps) {
  const merged =
    className !== undefined && className.length > 0
      ? `${header} ${className}`
      : header;
  return (
    <header ref={ref} className={merged} {...rest}>
      <div className={titleBlock}>
        <h1 className={titleClass}>{title}</h1>
        {subtitle !== undefined && subtitle !== null && (
          <p className={subtitleClass}>{subtitle}</p>
        )}
      </div>
      {actions !== undefined && actions !== null && (
        <div className={actionsClass}>{actions}</div>
      )}
    </header>
  );
}
